import { useState, useEffect } from "react";
import { useWallet } from "@/context/WalletContext";
import { useSocket } from "@/context/SocketContext";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { 
  Bell, 
  Trash2, 
  Plus, 
  ArrowUpRight, 
  Activity,
  ShieldAlert,
  Clock
} from "lucide-react";

type AlertType = "address" | "price";

interface AlertItem {
  id: string;
  type: AlertType;
  target: string;
  condition: string;
  value?: number;
  isActive: boolean;
  createdAt: number;
}

interface AlertLogItem {
  id: string;
  title: string;
  message: string;
  txSig?: string;
  createdAt: number;
}

export function AlertsPanel() {
  const { t } = useTranslation();
  const { xProfile } = useWallet() as any;
  const { socket } = useSocket();

  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [logs, setLogs] = useState<AlertLogItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Form states
  const [alertType, setAlertType] = useState<AlertType>("address");
  const [target, setTarget] = useState("");
  const [condition, setCondition] = useState("all");
  const [value, setValue] = useState("");

  // Fetch initial alerts and logs
  useEffect(() => {
    if (!xProfile) return;
    
    setLoading(true);
    Promise.all([
      fetch("/api/alerts").then(res => res.json()),
      fetch("/api/alerts/logs").then(res => res.json())
    ])
      .then(([alertsData, logsData]) => {
        if (Array.isArray(alertsData)) setAlerts(alertsData);
        if (Array.isArray(logsData)) setLogs(logsData);
      })
      .catch(err => console.error("Error loading alerts:", err))
      .finally(() => setLoading(false));
  }, [xProfile]);

  // Listen to live socket notifications for the user
  useEffect(() => {
    if (!socket || !xProfile?.id) return;

    const channelName = `notification-${xProfile.id}`;
    
    const handleNotification = (data: any) => {
      console.log("[SOCKET NOTIFICATION]:", data);
      if (data.type === "alert_triggered" || data.type === "price_alert_triggered") {
        // Prepend new logs
        const newLog: AlertLogItem = {
          id: `log-${Date.now()}`,
          title: data.title,
          message: data.message,
          txSig: data.trade?.signature,
          createdAt: Date.now()
        };
        setLogs(prev => [newLog, ...prev].slice(0, 50));
        
        // Refresh active alerts lists (as price alerts automatically deactivate after triggering)
        fetch("/api/alerts")
          .then(res => res.json())
          .then(data => {
            if (Array.isArray(data)) setAlerts(data);
          })
          .catch(() => {});
      }
    };

    socket.on(channelName, handleNotification);
    
    return () => {
      socket.off(channelName, handleNotification);
    };
  }, [socket, xProfile]);

  const handleCreateAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!xProfile) return;
    if (!target.trim()) return;

    try {
      const res = await fetch("/api/alerts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: alertType,
          target: target.trim(),
          condition: alertType === "price" ? condition : (condition === "all" ? "" : condition),
          value: value ? parseFloat(value) : 0
        })
      });

      if (res.ok) {
        const newAlert = await res.json();
        setAlerts(prev => [newAlert, ...prev]);
        setTarget("");
        setValue("");
        // Reload points task progress implicitly
      }
    } catch (err) {
      console.error("Failed to create alert:", err);
    }
  };

  const handleDeleteAlert = async (id: string) => {
    try {
      const res = await fetch(`/api/alerts/${id}`, {
        method: "DELETE"
      });
      if (res.ok) {
        setAlerts(prev => prev.filter(a => a.id !== id));
      }
    } catch (err) {
      console.error("Failed to delete alert:", err);
    }
  };

  if (!xProfile) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <ShieldAlert className="size-12 mb-3 text-violet-400 animate-pulse" />
        <h3 className="text-[16px] font-semibold text-white mb-1">
          {t("wallet.xAuthTitle")}
        </h3>
        <p className="text-[12px] text-violet-200/50 max-w-sm">
          {t("wallet.xPrompt")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col md:flex-row overflow-hidden bg-[#07070e] font-sans text-white md:divide-x md:divide-white/[0.06]">
      {/* Left panel: Alert Creator & Subscriptions */}
      <div className="flex flex-col w-full md:w-1/2 p-4 min-h-0 overflow-y-auto">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="size-5 text-purple-400" />
          <h2 className="text-[16px] font-bold bg-gradient-to-r from-teal-200 to-violet-200 bg-clip-text text-transparent">
            {t("alerts.panelTitle")}
          </h2>
        </div>

        {/* Creator Form */}
        <form onSubmit={handleCreateAlert} className="rounded-xl border border-white/[0.06] bg-[#0d0d18] p-4 mb-5 shadow-inner">
          <h3 className="text-[13px] font-bold text-cyan-300 mb-3">
            {t("alerts.createTitle")}
          </h3>

          <div className="flex items-center gap-2 mb-4 bg-black/40 p-1 rounded-lg border border-white/[0.04]">
            <button
              type="button"
              onClick={() => { setAlertType("address"); setCondition("all"); }}
              className={`flex-1 text-[11px] font-semibold py-1.5 rounded-md transition-all ${alertType === "address" ? "bg-purple-600/20 text-purple-300 border border-purple-500/30" : "text-white/40 hover:text-white/80"}`}
            >
              Whale Wallet Alert
            </button>
            <button
              type="button"
              onClick={() => { setAlertType("price"); setCondition("above"); }}
              className={`flex-1 text-[11px] font-semibold py-1.5 rounded-md transition-all ${alertType === "price" ? "bg-purple-600/20 text-purple-300 border border-purple-500/30" : "text-white/40 hover:text-white/80"}`}
            >
              Token Price Alert
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-[10px] uppercase font-bold tracking-wider text-violet-200/50 mb-1">
                {alertType === "address" ? t("alerts.walletLabel") : "Token Mint Address"}
              </label>
              <input
                type="text"
                placeholder={alertType === "address" ? "Solana address (e.g. Fp1...)" : "Token mint address"}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="w-full text-[12px] bg-black/50 border border-white/[0.08] rounded-lg px-3 py-2 text-white placeholder-white/20 focus:outline-none focus:border-cyan-400/50 font-mono"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase font-bold tracking-wider text-violet-200/50 mb-1">
                  {alertType === "address" ? "Action Trigger" : t("alerts.directionLabel")}
                </label>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="w-full text-[12px] bg-black/50 border border-white/[0.08] rounded-lg px-2 py-2 text-white focus:outline-none focus:border-cyan-400/50"
                >
                  {alertType === "address" ? (
                    <>
                      <option value="all">All Actions</option>
                      <option value="buy">Buys Only</option>
                      <option value="sell">Sells Only</option>
                    </>
                  ) : (
                    <>
                      <option value="above">Above Price</option>
                      <option value="below">Below Price</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase font-bold tracking-wider text-violet-200/50 mb-1">
                  {alertType === "address" ? t("alerts.minSizeLabel") : t("alerts.priceLabel")}
                </label>
                <input
                  type="number"
                  step="any"
                  placeholder="e.g. 0.1"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full text-[12px] bg-black/50 border border-white/[0.08] rounded-lg px-3 py-2 text-white placeholder-white/20 focus:outline-none focus:border-cyan-400/50 font-mono"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full flex items-center justify-center gap-1.5 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white font-bold py-2 rounded-lg text-[12px] tracking-wide transition-all shadow-[0_2px_8px_rgba(147,51,234,0.3)] mt-2"
            >
              <Plus className="size-4" />
              Add Alert Monitor
            </button>
          </div>
        </form>

        {/* Subscriptions List */}
        <div className="flex-1 min-h-[200px]">
          <h3 className="text-[12px] font-bold text-violet-200/50 uppercase tracking-wider mb-2">
            Active Monitors ({alerts.length})
          </h3>
          
          {loading ? (
            <div className="text-[12px] text-white/30 italic p-2">{t("panel.loading")}</div>
          ) : alerts.length === 0 ? (
            <div className="text-[11px] text-white/30 italic border border-dashed border-white/10 p-4 rounded-xl text-center">
              No alert monitors configured. Create one above to trace transactions!
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((alert) => (
                <div 
                  key={alert.id} 
                  className="flex items-center justify-between p-2.5 rounded-lg border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03] transition-colors"
                >
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="flex items-center gap-1.5">
                      <span className={`size-1.5 rounded-full ${alert.isActive ? "bg-emerald-400 animate-pulse" : "bg-white/20"}`} />
                      <span className="text-[11px] font-bold uppercase text-cyan-300">
                        {alert.type === "address" ? "Whale Alert" : "Price Alert"}
                      </span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-violet-200/60 font-semibold uppercase">
                        {alert.condition || "all"}
                      </span>
                      {alert.value ? (
                        <span className="text-[10px] font-mono text-purple-300">
                          {alert.value} SOL
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 font-mono text-[10px] text-white/50 truncate">
                      {alert.target}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteAlert(alert.id)}
                    className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-all shrink-0"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right panel: Live alerts stream */}
      <div className="flex flex-col w-full md:w-1/2 p-4 min-h-0 overflow-hidden">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="size-4 text-emerald-400 animate-pulse" />
          <h3 className="text-[13px] font-bold uppercase tracking-wider text-violet-200/50">
            {t("alerts.liveFeed")}
          </h3>
        </div>

        <div className="flex-1 min-h-[300px] overflow-y-auto bg-black/40 border border-white/[0.04] rounded-xl p-3 space-y-3 shadow-inner">
          {logs.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center p-6 text-white/20">
              <Clock className="size-8 mb-2 opacity-50" />
              <p className="text-[11px] italic">
                Waiting for trigger updates... Active alerts will report here instantly.
              </p>
            </div>
          ) : (
            logs.map((log) => (
              <div 
                key={log.id} 
                className="p-2.5 rounded-lg bg-[#0c0c18] border border-cyan-500/10 shadow-lg relative overflow-hidden group hover:border-cyan-400/30 transition-all"
              >
                <div className="absolute top-0 left-0 w-[2px] h-full bg-cyan-400" />
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[11px] font-bold text-white/90">{log.title}</div>
                  <span className="text-[9px] text-white/20 shrink-0 font-mono">
                    {new Date(log.createdAt).toLocaleTimeString()}
                  </span>
                </div>
                <p className="mt-1 text-[10.5px] leading-relaxed text-violet-200/70 font-sans">
                  {log.message}
                </p>
                {log.txSig ? (
                  <div className="mt-2 flex items-center justify-between">
                    <a
                      href={`https://solscan.io/tx/${log.txSig}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-cyan-400 hover:text-cyan-300 font-mono"
                    >
                      Explorer
                      <ArrowUpRight className="size-3" />
                    </a>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
